/* eslint-disable no-console */
import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Modal from 'apollo-react/components/Modal';
import PasswordInput from 'apollo-react/components/PasswordInput';
import TextField from 'apollo-react/components/TextField';
import Close from 'apollo-react-icons/Close';
import trim from 'lodash/trim';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { setIsWorkflowRequestPending } from '../actions/WorkflowActions';
import smartSolveApi from '../api/instances/smartsolve';
import { useTranslation } from '../custom-hooks/useTranslation';
import { useWindowDimensions } from '../custom-hooks/useWindowDimensions';
import { getUITA } from '../utils/commonUtils';

const useStyles = makeStyles({
  signOffHeader: {
    color: '#fff',
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: 1.5,
    marginTop: 6,
    textAlign: 'center',
    padding: '0px 75px',
  },
  signOffMeaning: {
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: 1.71,
    color: '#f8f9fb',
  },
  authErrorMessage: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 1.71,
    color: '#fff',
  },
  textInput: {
    width: '360px',
    color: '#f8f9fb',
  },
  textInputMobile: {
    width: '90vw',
    color: '#f8f9fb',
  },
  closeIconWrapper: {
    width: '100%',
    position: 'relative',
  },
  closeIcon: {
    color: 'white',
    stroke: 'white',
    strokeWidth: 3,
    float: 'right',
    fontSize: '18px',
    margin: '7px 24px',
    cursor: 'pointer',
    position: 'absolute',
    right: 10,
  },
  signOffButtonWrapper: {
    marginTop: '16px',
    marginBottom: '24px',
  },
  signOffButton: {
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: '#ebf2fc',
    },
  },
  signOffPaperFullWidth: {
    margin: 0,
    alignItems: 'center',
    width: '100%',
    maxWidth: '100%',
    bottom: 0,
    position: 'absolute',
    backgroundColor: '#0768fd !important',
    border: 'none !important',
    borderRadius: '0 !important',
    color: '#f8f9fb',

    '& > div:first-child': {
      display: 'none',
    },
    '& > div:last-child': {
      width: '100%',
    },
  },
  signOffDialog: {
    textAlign: 'center',
    width: '100%',
  },
  signatureAlert: {
    margin: '0 auto 1rem auto',
    lineHeight: '0.9rem',
    textAlign: 'center',
    maxWidth: 400,
    fontSize: '0.8rem',
    fontStyle: 'italic',
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
});

const AuthBlueBanner = ({
  open,
  signOffMeaningKey = '',
  handleDialogClose = () => {},
  onAuthSuccess = () => {},
  setAuthenticating = () => {},
  submitButtonLabel = 'Sign-Off',
  signOffStepName = '',
  isWorkflow = false,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const { isMobileDevice } = useWindowDimensions();

  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [authErrorMessage, setAuthErrorMessage] = useState('');

  useEffect(
    (open) => {
      if (!open) {
        setPassword('');
        setUsername('');
        setAuthErrorMessage('');
      }
    },
    [open]
  );

  const signOffPurpose = translateString('SignOffPurpose');
  const signOffPurposeMsg = translateString('SignOffPurposeMsg');
  const signatureAlert = translateString('SignatureAlert');
  const signOffMeaning = translateString(signOffMeaningKey);

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthenticating(true);
    isWorkflow && dispatch(setIsWorkflowRequestPending(true));

    try {
      const { data } = await smartSolveApi.post('/authentication', {
        password,
        username: trim(username),
      });

      setAuthenticating(false);

      if (data.hasError) {
        const error = data.errors[0];
        const errorMessage = error.errorMessage ?? '';
        const errorNumber = error.errorNumber ?? '';
        const newAuthErrorMessage = `${errorMessage}${errorNumber &&
          `${translateString('Code')}: ${errorNumber}`}`;

        isWorkflow && dispatch(setIsWorkflowRequestPending(false));
        setAuthErrorMessage(newAuthErrorMessage);
        return;
      }

      if (data.IsAuthenticated) {
        onAuthSuccess(password);
        setAuthErrorMessage('');
      } else {
        isWorkflow && dispatch(setIsWorkflowRequestPending(false));
        setAuthErrorMessage('Authentication is failed. (IsAuthenticated is false)');
      }
    } catch (error) {
      isWorkflow && dispatch(setIsWorkflowRequestPending(false));
      setIsWorkflowRequestPending(false);
      setAuthenticating(false);
      setAuthErrorMessage('Authentication is failed. (API call issue)');
      console.log(`/authentication${error.stack}`);
    }
  };

  return (
    <Modal
      open={open}
      fullWidth
      title=""
      classes={{ paperFullWidth: classes.signOffPaperFullWidth }}
      onClose={handleDialogClose}
      disableBackdropClick
      disableEscapeKeyDown
      hideButtons
    >
      <div className={classes.closeIconWrapper}>
        <Close className={classes.closeIcon} onClick={handleDialogClose} />
        <div className={classes.signOffHeader}>
          {signOffPurposeMsg
            ? `${signOffPurpose}: ${signOffPurposeMsg} ${signOffMeaning} ${signOffStepName}`
            : ''}
        </div>
      </div>
      <div className={classes.signOffDialog} ui-ta={getUITA('SSSignOff')}>
        {authErrorMessage ? (
          <div className={classes.authErrorMessage}>
            {translateString('Error')} {':'} {authErrorMessage}
          </div>
        ) : (
          ''
        )}
        <form onSubmit={handleAuthSubmit} className={classes.form}>
          <div>
            <span ui-ta={getUITA('FormCtrl', 'TextBox', 'Username')}>
              <TextField
                className={isMobileDevice ? classes.textInputMobile : classes.textInput}
                placeholder={translateString('Username')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                inputProps={{ autoComplete: 'off' }}
              />
            </span>
          </div>
          <div>
            <span ui-ta={getUITA('FormCtrl', 'TextBox', 'Password')}>
              <PasswordInput
                fullWidth
                className={isMobileDevice ? classes.textInputMobile : classes.textInput}
                placeholder={translateString('Password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                inputProps={{ autoComplete: 'off' }}
              />
            </span>
          </div>
          <div className={classes.signOffButtonWrapper}>
            <span ui-ta={getUITA('FormCtrl', 'Button', 'SignOff')}>
              <Button type="submit" variant="secondary" className={classes.signOffButton}>
                {translateString(submitButtonLabel)}
              </Button>
            </span>
          </div>
          <input type="submit" hidden />
        </form>
        <div className={classes.signatureAlert}>{signatureAlert}</div>
      </div>
    </Modal>
  );
};

export default AuthBlueBanner;
